import { useState, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useHotkeys } from '@mantine/hooks';
import {
  AppShell,
  Group,
  Image,
  Box,
  Paper,
  Text,
  Divider,
  HoverCard,
  TextInput,
  Burger,
  NavLink,
  ActionIcon,
  ScrollArea,
  LoadingOverlay,
  Tooltip,
  Menu,
  Stack,
} from '@mantine/core';
import {
  IconSearch,
  IconBrandAsana,
  IconAddressBook,
  IconBuildingStore,
  IconMap2,
  IconSocial,
  IconReportMoney,
  IconChevronLeft,
  IconUserCircle,
  IconMoon,
  IconSun,
  IconCash,
  IconTags,
  IconRoad,
  IconSteeringWheel,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react';
import logoDark from '../assets/logo.png';
import logoLight from '../assets/logo-light.png';
import { useDisclosure } from '@mantine/hooks';
import { useTheme } from '../context/ThemeProvider.tsx';
import useStaffInfo from '../hooks/staff/use-staff-info.ts';

const NAV_BAR_WIDTH: number = 240;
const NAV_BAR_COLLAPSED_WIDTH: number = 60;
const XL_WIDTH: number = 32;

export default function AppLayout() {
  const location = useLocation();
  const [isBurgerOpened, { toggle: toggleBurger }] = useDisclosure(true);
  const [isNavbarCompact, { toggle: toggleNavbarCompact }] =
    useDisclosure(false);
  const { colorScheme, toggleColorScheme } = useTheme();
  const { staff, isLoading: isStaffInfoLoading } = useStaffInfo();
  const [menuKeyword, setMenuKeyword] = useState<string>('');
  const menuSearchInputRef = useRef<HTMLInputElement>(null);

  useHotkeys([['mod+K', () => menuSearchInputRef.current?.focus()]]);

  if (isStaffInfoLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  const oldAdminUrl: string = import.meta.env.VITE_BASE_URL;

  const menus = [
    {
      label: '任務管理',
      icon: <IconBrandAsana size={18} />,
      path: '/missions',
      resource: 'MISSION',
    },
    {
      label: '業者管理',
      icon: <IconAddressBook size={18} />,
      path: '/partners',
      resource: 'PARTNER',
    },
    {
      label: '商店管理',
      icon: <IconBuildingStore size={18} />,
      path: '/stores',
      resource: 'STORE',
    },
    {
      label: '圖資管理',
      icon: <IconMap2 size={18} />,
      path: '/places',
      resource: 'PLACE',
    },
    {
      label: '品牌管理',
      icon: <IconSocial size={18} />,
      path: '/brands',
      resource: 'BRAND',
    },
    {
      label: '場站付款方式管理',
      icon: <IconCash size={18} />,
      path: '/payment-methods',
      resource: 'PAYMENT_METHOD',
    },
    {
      label: '標籤管理',
      icon: <IconTags size={18} />,
      path: '/tags',
      resource: 'TAG',
    },
    {
      label: '財務管理',
      icon: <IconReportMoney size={18} />,
      paths: [
        {
          label: '待確認對帳單',
          path: '/billing/unconfirmed',
          resource: 'MAAS_BILLING_PARKING_BILLING',
        },
        {
          label: '已確認對帳單',
          path: '/billing/confirmed',
          resource: 'MAAS_BILLING_PARKING_BILLING',
        },
        {
          label: '匯款單列表',
          path: '/remittance-instructions',
          resource: 'MAAS_BILLING_REMITTANCE_INSTRUCTION',
        },
        {
          label: '歷史對帳單',
          path: '/billing/history',
          resource: 'MAAS_BILLING_PARKING_BILLING',
        },
        {
          label: '廠商匯款帳號',
          path: '/site-providers',
          resource: 'MAAS_BILLING_SITE_PROVIDER',
        },
        {
          label: '對帳單設定',
          path: '/billing/settings',
          resource: 'MAAS_BILLING_PARKING_BILLING_SETTING',
        },
      ],
    },
    {
      label: '通行費自動繳費',
      icon: <IconRoad size={18} />,
      resource: 'COCKPIT_ETC_AUTO_PAY_TICKET',
      paths: [
        {
          label: '查繳任務',
          path: '/tickets/pending',
          resource: 'COCKPIT_ETC_AUTO_PAY_TICKET',
        },
        {
          label: '歷史任務',
          path: '/tickets/history',
          resource: 'COCKPIT_ETC_AUTO_PAY_TICKET',
        },
        {
          label: '通行費申請單',
          path: '/tickets/enrollments',
          resource: 'COCKPIT_ETC_AUTO_PAY_TICKET',
        },
      ],
    },
    {
      label: '駕駛管理',
      icon: <IconSteeringWheel size={18} />,
      resource: 'COCKPIT_ACCOUNT',
      paths: [
        {
          label: '駕駛資料審核',
          path: '/driver-management/document-review',
          resource: 'COCKPIT_ACCOUNT_DOCUMENT',
        },
        {
          label: '駕駛人資料',
          path: '/driver-management/drivers',
          resource: 'COCKPIT_ACCOUNT',
        },
      ],
    },
  ]
    .filter((item) => {
      // 根據擁有的權限篩選淺層資源
      if (item.path) {
        return (
          staff.permissions[item.resource]?.read ||
          staff.permissions[item.resource]?.write
        );
      } else {
        return item.paths.some(
          (nestedItem) =>
            staff.permissions[nestedItem.resource]?.read ||
            staff.permissions[nestedItem.resource]?.write,
        );
      }
    })
    .map((item) => {
      // 根據擁有的權限決定暴露的深層資源
      if (item.path) {
        return item;
      } else {
        return {
          ...item,
          paths: item.paths.filter(
            (nestedItem) =>
              staff.permissions[nestedItem.resource]?.read ||
              staff.permissions[nestedItem.resource]?.write,
          ),
        };
      }
    })
    .filter(
      (item) =>
        // 根據搜尋欄位篩選淺層資源
        item.label.includes(menuKeyword) ||
        item.paths?.some((nestedItem) =>
          nestedItem.label.includes(menuKeyword),
        ),
    )
    .map((item) => {
      // 根據搜尋欄位決定暴露的深層資源
      if (item.path) {
        return item;
      } else {
        return {
          ...item,
          paths: item.paths.filter((nestedItem) =>
            nestedItem.label.includes(menuKeyword),
          ),
        };
      }
    });

  const navbarWidth = isNavbarCompact ? NAV_BAR_COLLAPSED_WIDTH : NAV_BAR_WIDTH;

  const userAvatarAction = (
    <ActionIcon
      w={22}
      h={22}
      variant="light"
      radius="xl"
      style={{ border: '1px solid lightgray' }}
    >
      <IconUserCircle size={18} />
    </ActionIcon>
  );

  const themeToggleAction = (
    <ActionIcon
      variant="transparent"
      c={colorScheme === 'light' ? 'black' : 'white'}
      onClick={(event) => {
        event.stopPropagation();
        toggleColorScheme();
      }}
    >
      {(colorScheme === 'light' && <IconMoon size={20} />) || (
        <IconSun size={20} />
      )}
    </ActionIcon>
  );

  const staffEmailHoverCardDropdown = (
    <HoverCard.Dropdown>
      <Paper>
        <Text fz={12} c="dimmed">
          email
        </Text>
        <Text fz={12}>{staff.email}</Text>
      </Paper>
    </HoverCard.Dropdown>
  );

  const interactiveTargetStyles = (theme: {
    spacing: { sm: string };
    radius: { sm: string };
    colors: { dark: string[]; gray: string[] };
  }) => ({
    padding: theme.spacing.sm,
    borderRadius: theme.radius.sm,
    cursor: 'pointer' as const,
    '&:hover': {
      backgroundColor:
        colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
    },
  });
  const compactNavLinkStyles = isNavbarCompact
    ? {
        root: { justifyContent: 'center' as const },
        section: { marginInlineEnd: 0 },
        label: { display: 'none' as const },
      }
    : undefined;

  const renderMenuItem = (item: (typeof menus)[number]) => {
    if (item.path) {
      const navLink = (
        <NavLink
          key={item.path}
          leftSection={item.icon}
          label={item.label}
          component={Link}
          to={item.path}
          variant="light"
          active={location.pathname.startsWith(item.path)}
          styles={compactNavLinkStyles}
        />
      );

      if (!isNavbarCompact) {
        return navLink;
      }

      return (
        <Tooltip key={item.path} label={item.label} position="right">
          {navLink}
        </Tooltip>
      );
    }

    const isNestedActive = item.paths?.some((nestedItem) =>
      location.pathname.startsWith(nestedItem.path),
    );

    if (!isNavbarCompact) {
      const nestedNavLinks = item.paths?.map((nestedItem) => (
        <NavLink
          key={nestedItem.path}
          label={nestedItem.label}
          component={Link}
          to={nestedItem.path}
          variant="light"
          active={location.pathname.startsWith(nestedItem.path)}
        />
      ));

      return (
        <NavLink
          key={item.label}
          label={item.label}
          leftSection={item.icon}
          childrenOffset={28}
          variant="subtle"
          defaultOpened
          active={isNestedActive}
        >
          {nestedNavLinks}
        </NavLink>
      );
    }

    return (
      <Menu
        key={item.label}
        trigger="hover"
        position="right-start"
        offset={8}
        withinPortal
      >
        <Menu.Target>
          <Tooltip label={item.label} position="right">
            <NavLink
              leftSection={item.icon}
              label={item.label}
              variant="light"
              active={isNestedActive}
              styles={compactNavLinkStyles}
            />
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          {item.paths?.map((nestedItem) => (
            <Menu.Item
              key={nestedItem.path}
              component={Link}
              to={nestedItem.path}
              fw={
                location.pathname.startsWith(nestedItem.path) ? 600 : undefined
              }
            >
              {nestedItem.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    );
  };

  return (
    <AppShell
      navbar={{
        width: navbarWidth,
        breakpoint: 'sm',
        collapsed: { mobile: !isBurgerOpened, desktop: false },
      }}
    >
      <AppShell.Navbar withBorder style={{ height: '100vh' }}>
        <Box px={isNavbarCompact ? 'xs' : 'md'} pt="xs" w="100%">
          {isNavbarCompact ? (
            <>
              <Group justify="center" hiddenFrom="sm">
                <Burger
                  opened={isBurgerOpened}
                  onClick={toggleBurger}
                  size="sm"
                />
              </Group>
              <Group justify="center" w="100%" visibleFrom="sm">
                <Tooltip label="展開側欄" position="right">
                  <ActionIcon
                    variant="subtle"
                    onClick={toggleNavbarCompact}
                    aria-label="展開側欄"
                  >
                    <IconLayoutSidebarLeftExpand
                      size={18}
                      color={colorScheme === 'dark' ? 'white' : 'black'}
                    />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </>
          ) : (
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Burger
                  opened={isBurgerOpened}
                  onClick={toggleBurger}
                  hiddenFrom="sm"
                  size="sm"
                />
                <Box component={Link} to="/">
                  <Image
                    src={colorScheme === 'light' ? logoDark : logoLight}
                    alt="Logo"
                    height={50}
                    px="md"
                    fit="contain"
                  />
                </Box>
              </Group>
              <Tooltip label="收合側欄" position="right">
                <ActionIcon
                  variant="subtle"
                  onClick={toggleNavbarCompact}
                  visibleFrom="sm"
                  aria-label="收合側欄"
                >
                  <IconLayoutSidebarLeftCollapse
                    size={18}
                    color={colorScheme === 'dark' ? 'white' : 'black'}
                  />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Box>

        {!isNavbarCompact && (
          <TextInput
            px="md"
            pb={10}
            ref={menuSearchInputRef}
            leftSection={<IconSearch size={20} />}
            placeholder="ctrl + K 以搜尋"
            value={menuKeyword}
            onChange={(e) => setMenuKeyword(e.currentTarget.value)}
          />
        )}

        <ScrollArea
          px={isNavbarCompact ? 'xs' : 'md'}
          pb="xs"
          style={{ flex: 1 }}
        >
          {menus.map(renderMenuItem)}
        </ScrollArea>

        <Box px={isNavbarCompact ? 'xs' : 'md'} pb={4}>
          {isNavbarCompact ? (
            <Tooltip label="點擊返回舊後台" position="right">
              <NavLink
                leftSection={<IconChevronLeft size={18} />}
                key={oldAdminUrl}
                component={Link}
                to={oldAdminUrl}
                variant="light"
                label="點擊返回舊後台"
                styles={compactNavLinkStyles}
              />
            </Tooltip>
          ) : (
            <NavLink
              label="點擊返回舊後台"
              leftSection={<IconChevronLeft size={18} />}
              key={oldAdminUrl}
              component={Link}
              to={oldAdminUrl}
              variant="light"
            />
          )}
        </Box>

        <Divider mx="xs" />
        {isNavbarCompact ? (
          <Stack gap="xs" align="center" p="xs" w="100%">
            <HoverCard shadow="md" openDelay={200} position="right">
              <HoverCard.Target>
                <Box style={interactiveTargetStyles}>{userAvatarAction}</Box>
              </HoverCard.Target>
              {staffEmailHoverCardDropdown}
            </HoverCard>
            {themeToggleAction}
          </Stack>
        ) : (
          <HoverCard shadow="md" openDelay={200} position="right">
            <HoverCard.Target>
              <Group
                gap={4}
                p="xs"
                h={48}
                align="center"
                justify="space-between"
                wrap="nowrap"
                style={interactiveTargetStyles}
              >
                {userAvatarAction}

                <Box w={100}>
                  <Text
                    fz={14}
                    c={colorScheme === 'dark' ? 'white' : 'black'}
                    truncate="end"
                  >
                    {staff.name}
                  </Text>
                </Box>

                <Group align="center" flex={1} justify="flex-end">
                  {themeToggleAction}
                </Group>
              </Group>
            </HoverCard.Target>
            {staffEmailHoverCardDropdown}
          </HoverCard>
        )}
      </AppShell.Navbar>
      <AppShell.Main pl={navbarWidth + XL_WIDTH} pr="xl" py="lg">
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
